import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { AppPicker } from './AppPicker.js';
import { Button, DataTable } from '../../../../shared/components/index.js';

interface VersionRow {
  readonly versionId: string;
  readonly versionCode: string;
  readonly versionNo: number;
  readonly versionLabel: string;
  readonly changeSummary: string;
  readonly isCurrent: boolean;
  readonly status: string;
  readonly publishedAt: string | null;
}

const STATUSES = ['DRAFT', 'PUBLISHED', 'SUPERSEDED', 'ARCHIVED'];

/**
 * M40 — Version Management Dashboard (§19). The release surface over T532.
 * Cut a new version of an application (immutable snapshot), publish it as the
 * current release, or roll back to a prior one (BR-04 / BR-05). Viewing needs
 * VERSION_VIEW; cut/publish need VERSION_MANAGE; rollback needs APP_ROLLBACK.
 */
export function VersionManagementDashboard(): ReactNode {
  const { token } = useAuth();
  const { t } = useTranslation();
  const [applicationId, setApplicationId] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [rows, setRows] = useState<VersionRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);

  const [versionLabel, setVersionLabel] = useState('');
  const [changeSummary, setChangeSummary] = useState('');
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (applicationId) params.set('applicationId', applicationId);
    if (statusFilter) params.set('status', statusFilter);
    params.set('pageSize', '100');
    apiRequest<{ data: VersionRow[]; total: number }>(`/v1/app-platform/versions?${params.toString()}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, applicationId, statusFilter, reload]);

  const create = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setActionError(null);
    setNotice(null);
    if (!applicationId.trim()) { setActionError(t('m40.common.appIdRequired')); return; }
    if (!versionLabel.trim()) { setActionError(t('m40.version.labelRequired')); return; }
    setBusy(true);
    try {
      const r = await apiRequest<{ data: VersionRow }>(`/v1/app-platform/versions`, {
        method: 'POST',
        body: { applicationId: applicationId.trim(), versionLabel: versionLabel.trim(), changeSummary: changeSummary.trim() || undefined },
        token,
      });
      setNotice(t('m40.version.cut', { code: r.data.versionCode }));
      setVersionLabel('');
      setChangeSummary('');
      setReload((n) => n + 1);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setActionError(t('m40.version.notAuthManage'));
      } else {
        setActionError((err as Error).message);
      }
    } finally {
      setBusy(false);
    }
  };

  const publish = async (row: VersionRow): Promise<void> => {
    setActionError(null);
    setNotice(null);
    try {
      await apiRequest(`/v1/app-platform/versions/${row.versionId}/publish`, { method: 'POST', token });
      setNotice(t('m40.common.published', { code: row.versionCode }));
      setReload((n) => n + 1);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setActionError(t('m40.version.notAuthManage'));
      } else {
        setActionError((err as Error).message);
      }
    }
  };

  const rollback = async (row: VersionRow): Promise<void> => {
    setActionError(null);
    setNotice(null);
    try {
      await apiRequest(`/v1/app-platform/versions/${row.versionId}/rollback`, { method: 'POST', token });
      setNotice(t('m40.version.rolledBack', { code: row.versionCode }));
      setReload((n) => n + 1);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setActionError(t('m40.version.notAuthRollback'));
      } else {
        setActionError((err as Error).message);
      }
    }
  };

  return (
    <main aria-label="version-management">
      <h1>{t('m40.version.title')}</h1>

      <section aria-label="version-scope">
        <AppPicker label={t('m40.common.applicationId')} value={applicationId} onPick={setApplicationId} />
      </section>

      <section aria-label="create-version">
        <h2>{t('m40.version.cutHeading')}</h2>
        <form aria-label="version-form" onSubmit={(e) => void create(e)}>
          <label>{t('m40.version.labelField')}<input aria-label="field-version-label" value={versionLabel} onChange={(e) => setVersionLabel(e.target.value)} /></label>
          <label>{t('m40.version.changeSummary')}<input aria-label="field-change-summary" value={changeSummary} onChange={(e) => setChangeSummary(e.target.value)} /></label>
          <Button variant="primary" type="submit" aria-label="submit-version" disabled={busy}>{busy ? t('m40.version.cutting') : t('m40.version.cutBtn')}</Button>
        </form>
        {actionError && <p role="alert" aria-label="version-error">{actionError}</p>}
        {notice && <p aria-label="version-notice">{notice}</p>}
      </section>

      <section aria-label="version-filters">
        <label>{t('m40.common.status')}
          <select aria-label="filter-status" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="">{t('m40.common.allStates')}</option>
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </label>
      </section>

      {error && <p role="alert">{error}</p>}
      {loading ? <p>{t('m40.common.loading')}</p> : (
        <>
          <p aria-label="version-total">{t('m40.version.total', { count: rows.length })}</p>
          <DataTable<VersionRow>
            ariaLabel="version-table"
            testId="version-table"
            rowKey={(r) => r.versionId}
            rowDataAttrs={(r) => ({ 'data-version': r.versionCode })}
            emptyMessage={t('m40.version.empty')}
            rows={rows}
            columns={[
              { key: 'code', header: t('m40.common.code'), render: (r) => r.versionCode },
              { key: 'no', header: t('m40.version.colNo'), render: (r) => `v${r.versionNo}` },
              { key: 'label', header: t('m40.version.colLabel'), render: (r) => r.versionLabel },
              { key: 'status', header: t('m40.common.status'), cellAriaLabel: (r) => `status-${r.versionCode}`, render: (r) => r.status },
              { key: 'current', header: t('m40.version.colCurrent'), cellAriaLabel: (r) => `current-${r.versionCode}`, render: (r) => r.isCurrent ? t('m40.common.yes') : t('m40.common.no') },
              { key: 'actions', header: t('m40.common.actions'), render: (r) => (
                <>
                  <Button variant="primary" type="button" aria-label={`publish-${r.versionCode}`} onClick={() => void publish(r)}>{t('m40.common.publish')}</Button>
                  <Button variant="danger" type="button" aria-label={`rollback-${r.versionCode}`} onClick={() => void rollback(r)}>{t('m40.common.rollback')}</Button>
                </>
              ) },
            ]}
          />
        </>
      )}
    </main>
  );
}
